import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AppRole, Quadrant } from "@msc/shared";

import { AbsencesTab } from "../components/absences/AbsencesTab.js";
import { BetriebPanel } from "../components/settings/BetriebPanel.js";
import { ApiTokensCard } from "../components/profile/ApiTokensCard.js";
import { MaintenanceImportTab } from "../components/maintenance/MaintenanceImportTab.js";
import { SoftwareCatalogTab } from "../components/maintenance/SoftwareCatalogTab.js";
import { SystemAttributeDefsTab } from "../components/maintenance/SystemAttributeDefsTab.js";
import { hasRole, useCurrentUser } from "../hooks/useAuth.js";
import { useSegments } from "../hooks/useSegments.js";
import { useFrequencies, useSetFrequency } from "../hooks/useSettings.js";
import {
  useCreateEmployee,
  useEmployeeProfile,
  useGrantRole,
  useRevokeRole,
  useSaveEmployeeProfile,
  useSetUserActive,
  useSetUserName,
  useUsers,
  type UserListItem,
  type UserRoleEntry,
} from "../hooks/useUsers.js";
import { useEmployees } from "../hooks/useEmployees.js";
import { FEDERAL_STATES } from "@msc/shared";

function useRoleLabels(): Record<AppRole, string> {
  const { t } = useTranslation();
  return {
    admin: t("roles.admin"),
    geschaeftsleitung: t("roles.geschaeftsleitung"),
    bereichsleiter: t("roles.bereichsleiter"),
    stellvertreter: t("roles.stellvertreter"),
    projektleiter: t("roles.projektleiter"),
  };
}

/**
 * Vergebbare Rollen. `projektleiter` steht bewusst NICHT darin: Sie hängt an
 * den Servicebeauftragungen und entsteht bei jeder Anmeldung neu. Von Hand
 * gesetzt wäre sie eine zweite Wahrheit daneben — sie bliebe stehen, wenn die
 * Beauftragung längst jemand anderem gehört. Die API weist sie ebenfalls ab
 * (`ROLE_DERIVED_ONLY`); diese Liste erspart nur den vergeblichen Versuch.
 */
const ROLE_ORDER: AppRole[] = [
  "admin",
  "geschaeftsleitung",
  "bereichsleiter",
  "stellvertreter",
];

const ROLE_COLORS: Record<AppRole, string> = {
  admin: "red",
  geschaeftsleitung: "violet",
  bereichsleiter: "blue",
  stellvertreter: "cyan",
  projektleiter: "teal",
};

const ROLE_REQUIRES_SEGMENT: Record<AppRole, boolean> = {
  admin: false,
  geschaeftsleitung: false,
  bereichsleiter: true,
  stellvertreter: true,
  projektleiter: false,
};

export function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  return (
    <Stack gap="md">
      <Title order={2}>{t("pages.settings.title")}</Title>
      <Text c="dimmed" size="sm">
        {t("pages.settings.subtitle")}
      </Text>

      {/* BCG-Schwellen wohnen bewusst NUR auf der BCG-Matrix (Drawer im
          Perioden-Kontext) — der frühere Settings-Tab schrieb stur auf
          rolling_12m und divergierte von dem, was die Matrix zeigte. */}
      <Alert color="blue" variant="light">
        {t("pages.settings.thresholdsMovedHint")}{" "}
        <Anchor component={Link} to="/bcg" size="sm">
          {t("pages.settings.thresholdsMovedLink")}
        </Anchor>
      </Alert>

      <Tabs defaultValue="frequencies">
        <Tabs.List>
          <Tabs.Tab value="frequencies">{t("pages.settings.tabFrequencies")}</Tabs.Tab>
          <Tabs.Tab value="catalog">{t("pages.settings.tabCatalog")}</Tabs.Tab>
          <Tabs.Tab value="attributes">{t("pages.settings.tabAttributes")}</Tabs.Tab>
          <Tabs.Tab value="import">{t("pages.settings.tabImport")}</Tabs.Tab>
          <Tabs.Tab value="absences">{t("pages.settings.tabAbsences")}</Tabs.Tab>
          <Tabs.Tab value="betrieb">{t("pages.settings.tabBetrieb")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="frequencies" pt="md">
          <FrequenciesSettings />
        </Tabs.Panel>

        {/* Sichtbarkeit (Admin/BL/Stellv.) kommt bereits von der Route
            `/settings` selbst (RequireRole in router.ts) — kein eigenes Gate
            hier nötig. Schreiben bleibt für jeden Angemeldeten offen (Spec E6);
            das betrifft nur dieses Aufräumwerkzeug, nicht die API. */}
        <Tabs.Panel value="catalog" pt="md">
          <SoftwareCatalogTab />
        </Tabs.Panel>

        <Tabs.Panel value="attributes" pt="md">
          <SystemAttributeDefsTab />
        </Tabs.Panel>

        {/* Der Excel-Import wohnt bei den übrigen Wartungs-Stammdaten: Die
            Route `/settings` setzt Admin/BL/Stellv. voraus und deckt sich
            damit mit dem Rollen-Gate der Import-Endpunkte. */}
        <Tabs.Panel value="import" pt="md" keepMounted={false}>
          <MaintenanceImportTab />
        </Tabs.Panel>

        {/* Abwesenheiten von Hand: dasselbe Rollen-Gate wie der Import — die
            Route `/settings` setzt Admin/BL/Stellv. voraus, und genau diese
            Rollen lassen die Endpunkte zu. */}
        <Tabs.Panel value="absences" pt="md" keepMounted={false}>
          <AbsencesTab />
        </Tabs.Panel>

        {/* Betriebsstatus sieht jeder, der die Einstellungen öffnen darf; die
            Logzeilen nur `admin` (die API sperrt sie ohnehin mit 403). */}
        <Tabs.Panel value="betrieb" pt="md" keepMounted={false}>
          <BetriebPanel admin={hasRole(user, "admin")} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ── Vor-Ort-Frequenzen ───────────────────────────────────────────────────────

const QUADRANTS: Quadrant[] = ["star", "cash_cow", "question_mark", "dog"];

function useQuadrantLabel() {
  const { t } = useTranslation();
  return (q: Quadrant) => t(`quadrant.${q}`);
}

function FrequenciesSettings() {
  const { t } = useTranslation();
  const { data: segments } = useSegments();
  // segmentId === null = globale Defaults zeigen
  const [segmentId, setSegmentId] = useState<string | null>(null);

  const { data: frequencies, isLoading } = useFrequencies(segmentId);
  const setFrequency = useSetFrequency();

  // Map Quadrant → months für die aktuelle Auswahl
  const byQuadrant = new Map<Quadrant, number | null>();
  for (const f of frequencies ?? []) {
    byQuadrant.set(f.quadrant, f.months);
  }

  return (
    <Stack gap="md">
      <Group gap="sm">
        <Select
          label={t("pages.settings.frequencies.segmentLabel")}
          description={
            segmentId === null
              ? t("pages.settings.frequencies.segmentDefaultDescription")
              : t("pages.settings.frequencies.segmentOverrideDescription")
          }
          placeholder={t("pages.settings.frequencies.segmentDefaults")}
          data={[
            { value: "__default__", label: t("pages.settings.frequencies.segmentDefaults") },
            ...(segments ?? []).map((s) => ({ value: s.id, label: s.name })),
          ]}
          value={segmentId === null ? "__default__" : segmentId}
          onChange={(v) =>
            setSegmentId(v === "__default__" || v === null ? null : v)
          }
          style={{ minWidth: 280 }}
          clearable={false}
        />
      </Group>

      <Paper p="md" withBorder>
        {isLoading ? (
          <Skeleton height={180} />
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("pages.settings.frequencies.tableQuadrant")}</Table.Th>
                <Table.Th style={{ width: 200 }}>{t("pages.settings.frequencies.tableMonths")}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t("pages.settings.frequencies.tableAction")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {QUADRANTS.map((q) => (
                <FrequencyRow
                  key={q}
                  segmentId={segmentId}
                  quadrant={q}
                  initialMonths={byQuadrant.get(q) ?? null}
                  setFrequency={setFrequency}
                />
              ))}
            </Table.Tbody>
          </Table>
        )}

        {setFrequency.isError && (
          <Alert color="red" title={t("common.error")} mt="sm">
            {setFrequency.error instanceof Error
              ? setFrequency.error.message
              : t("pages.settings.frequencies.saveError")}
          </Alert>
        )}
      </Paper>
    </Stack>
  );
}

function FrequencyRow({
  segmentId,
  quadrant,
  initialMonths,
  setFrequency,
}: {
  segmentId: string | null;
  quadrant: Quadrant;
  initialMonths: number | null;
  setFrequency: ReturnType<typeof useSetFrequency>;
}) {
  const { t } = useTranslation();
  const quadrantLabel = useQuadrantLabel();
  const [months, setMonths] = useState<string | number>(initialMonths ?? "");

  useEffect(() => {
    setMonths(initialMonths ?? "");
  }, [initialMonths, segmentId, quadrant]);

  const monthsNum =
    typeof months === "number" ? months : months === "" ? null : parseFloat(months);

  const dirty =
    (monthsNum ?? null) !== (initialMonths ?? null);

  async function handleSave() {
    await setFrequency.mutateAsync({
      segmentId,
      quadrant,
      months: monthsNum !== null && !Number.isNaN(monthsNum) ? monthsNum : null,
    });
  }

  return (
    <Table.Tr>
      <Table.Td>{quadrantLabel(quadrant)}</Table.Td>
      <Table.Td>
        <NumberInput
          value={months}
          onChange={setMonths}
          min={1}
          max={36}
          placeholder={quadrant === "dog" ? t("pages.settings.frequencies.dogPlaceholder") : ""}
          allowDecimal={false}
        />
      </Table.Td>
      <Table.Td>
        <Button
          size="xs"
          variant="light"
          disabled={!dirty}
          loading={setFrequency.isPending}
          onClick={() => void handleSave()}
        >
          {t("pages.settings.frequencies.saveButton")}
        </Button>
      </Table.Td>
    </Table.Tr>
  );
}

export function SettingsUsersPage() {
  const { t } = useTranslation();
  const roleLabels = useRoleLabels();
  const { data: users, isLoading } = useUsers();
  const { data: segments } = useSegments();
  const revokeRole = useRevokeRole();
  const setActive = useSetUserActive();

  const [grantTarget, setGrantTarget] = useState<UserListItem | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{
    user: UserListItem;
    role: UserRoleEntry;
  } | null>(null);
  const [editNameTarget, setEditNameTarget] = useState<UserListItem | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [profileTarget, setProfileTarget] = useState<UserListItem | null>(null);

  const segmentNameById = new Map((segments ?? []).map((s) => [s.id, s.name]));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={2}>{t("pages.settings.users.title")}</Title>
          <Text c="dimmed" size="sm">
            {t("pages.settings.users.subtitle")}
          </Text>
        </Box>
        <Button size="xs" onClick={() => setCreateOpen(true)}>
          {t("pages.settings.users.createEmployeeButton")}
        </Button>
      </Group>

      <Paper p="md" withBorder>
        <Box style={{ overflowX: "auto" }}>
          <Table highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("pages.settings.users.tableEmployee")}</Table.Th>
                <Table.Th>{t("pages.settings.users.tableRoles")}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t("pages.settings.users.tableActive")}</Table.Th>
                <Table.Th style={{ width: 160 }}>{t("pages.settings.users.tableAction")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <Table.Tr key={i}>
                      {Array.from({ length: 4 }).map((_, j) => (
                        <Table.Td key={j}>
                          <Skeleton height={18} radius="sm" />
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))
                : (users ?? []).map((u) => (
                    <Table.Tr key={u.id}>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text fw={500} size="sm">
                            {u.fullName}
                          </Text>
                          <Text c="dimmed" size="xs">
                            {u.email}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        {u.roles.length === 0 && u.leadSegmentIds.length === 0 ? (
                          <Text c="dimmed" size="xs">
                            {t("pages.settings.users.rolesEmpty")}
                          </Text>
                        ) : (
                          <Group gap={4}>
                            {/*
                              Abgeleitete Rolle aus den Beauftragungen: sichtbar,
                              aber nicht anklickbar — es gibt nichts zu entziehen.
                              Der Weg führt über die Beauftragung.
                            */}
                            {u.leadSegmentIds.map((segmentId) => (
                              <Tooltip
                                key={`lead-${segmentId}`}
                                label={t("pages.settings.users.abgeleiteteRolleTooltip")}
                              >
                                <Badge
                                  color={ROLE_COLORS.projektleiter}
                                  variant="outline"
                                  size="sm"
                                >
                                  {roleLabels.projektleiter} ·{" "}
                                  {segmentNameById.get(segmentId) ?? "?"} ·{" "}
                                  {t("pages.settings.users.abgeleitet")}
                                </Badge>
                              </Tooltip>
                            ))}
                            {u.roles.map((r) => (
                              <Tooltip
                                key={r.id}
                                label={
                                  r.segmentId
                                    ? t("pages.settings.users.revokeWithSegmentTooltip", {
                                        segment: segmentNameById.get(r.segmentId) ?? "?",
                                      })
                                    : t("pages.settings.users.revokeTooltip")
                                }
                              >
                                <Badge
                                  color={ROLE_COLORS[r.role]}
                                  variant="light"
                                  size="sm"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => setRevokeTarget({ user: u, role: r })}
                                >
                                  {roleLabels[r.role]}
                                  {r.segmentId
                                    ? ` · ${segmentNameById.get(r.segmentId) ?? "?"}`
                                    : ""}
                                </Badge>
                              </Tooltip>
                            ))}
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Switch
                          checked={u.active}
                          onChange={(e) =>
                            setActive.mutate({
                              employeeId: u.id,
                              active: e.currentTarget.checked,
                            })
                          }
                          aria-label={
                            u.active
                              ? t("pages.settings.users.deactivateAria")
                              : t("pages.settings.users.activateAria")
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => setEditNameTarget(u)}
                          >
                            {t("pages.settings.users.editButton")}
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => setGrantTarget(u)}
                          >
                            {t("pages.settings.users.addRoleButton")}
                          </Button>
                          <Button
                            size="xs"
                            variant="subtle"
                            onClick={() => setProfileTarget(u)}
                          >
                            {t("pages.settings.users.profileButton")}
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
              {!isLoading && (users ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("pages.settings.users.noUsers")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Box>
      </Paper>

      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Title order={4}>{t("apiTokens.adminTitle")}</Title>
          <Text c="dimmed" size="sm">
            {t("apiTokens.adminHint")}
          </Text>
          <ApiTokensCard admin />
        </Stack>
      </Paper>

      <GrantRoleModal
        target={grantTarget}
        onClose={() => setGrantTarget(null)}
        segmentNameById={segmentNameById}
      />

      <RevokeRoleConfirm
        target={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        revokeRole={revokeRole}
        segmentNameById={segmentNameById}
      />

      <EditNameModal
        target={editNameTarget}
        onClose={() => setEditNameTarget(null)}
      />

      <CreateEmployeeModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <EmployeeProfileModal
        target={profileTarget}
        onClose={() => setProfileTarget(null)}
      />
    </Stack>
  );
}

// ── Edit Name (Admin) ────────────────────────────────────────────────────────

function EditNameModal({
  target,
  onClose,
}: {
  target: UserListItem | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const setName = useSetUserName();
  const [name, setName_] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName_(target?.fullName ?? "");
    setError(null);
  }, [target]);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!target) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("pages.settings.users.editNameError"));
      return;
    }
    if (trimmed === target.fullName) {
      handleClose();
      return;
    }
    try {
      await setName.mutateAsync({ employeeId: target.id, fullName: trimmed });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.settings.users.editNameSaveError"));
    }
  }

  return (
    <Modal
      opened={Boolean(target)}
      onClose={handleClose}
      title={t("pages.settings.users.editNameTitle")}
      size="sm"
      centered
    >
      <Stack gap="md">
        {target && (
          <Text size="xs" c="dimmed">
            {target.email}
          </Text>
        )}
        <TextInput
          label={t("pages.settings.users.editNameLabel")}
          value={name}
          onChange={(e) => setName_(e.currentTarget.value)}
          autoFocus
          required
        />
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={setName.isPending}>
            {t("common.cancel")}
          </Button>
          <Button loading={setName.isPending} onClick={() => void handleSubmit()}>
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Grant Modal ──────────────────────────────────────────────────────────────

function GrantRoleModal({
  target,
  onClose,
  segmentNameById,
}: {
  target: UserListItem | null;
  onClose: () => void;
  segmentNameById: Map<string, string>;
}) {
  const { t } = useTranslation();
  const roleLabels = useRoleLabels();
  const { data: segments } = useSegments();
  const grant = useGrantRole();

  const [role, setRole] = useState<AppRole | null>(null);
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleClose() {
    setRole(null);
    setSegmentId(null);
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!target || !role) return;
    if (ROLE_REQUIRES_SEGMENT[role] && !segmentId) {
      setError(t("pages.settings.users.grantNeedsSegment"));
      return;
    }
    try {
      await grant.mutateAsync({
        employeeId: target.id,
        role,
        segmentId: ROLE_REQUIRES_SEGMENT[role] ? segmentId ?? undefined : undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.settings.users.grantError"));
    }
  }

  const requiresSegment = role ? ROLE_REQUIRES_SEGMENT[role] : false;

  return (
    <Modal
      opened={Boolean(target)}
      onClose={handleClose}
      title={
        target
          ? t("pages.settings.users.grantTitle", { name: target.fullName })
          : t("pages.settings.users.grantTitleEmpty")
      }
      size="sm"
      centered
    >
      <Stack gap="md">
        <Select
          label={t("pages.settings.users.grantRoleLabel")}
          required
          placeholder={t("pages.settings.users.grantRolePlaceholder")}
          data={ROLE_ORDER.map((r) => ({
            value: r,
            label: roleLabels[r],
          }))}
          value={role}
          onChange={(v) => {
            setRole(v as AppRole | null);
            setSegmentId(null);
            setError(null);
          }}
        />
        {requiresSegment && (
          <Select
            label={t("pages.settings.users.grantSegmentLabel")}
            required
            placeholder={t("pages.settings.users.grantSegmentPlaceholder")}
            data={(segments ?? []).map((s) => ({ value: s.id, label: s.name }))}
            value={segmentId}
            onChange={setSegmentId}
          />
        )}
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={grant.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={!role}
            loading={grant.isPending}
          >
            {t("pages.settings.users.grantSubmit")}
          </Button>
        </Group>
        {role === "bereichsleiter" && segmentId && (
          <Text size="xs" c="dimmed">
            {t("pages.settings.users.grantBlHint", {
              segment: segmentNameById.get(segmentId) ?? "",
            })}
          </Text>
        )}
      </Stack>
    </Modal>
  );
}

// ── Revoke Confirm ───────────────────────────────────────────────────────────

function RevokeRoleConfirm({
  target,
  onClose,
  revokeRole,
  segmentNameById,
}: {
  target: { user: UserListItem; role: UserRoleEntry } | null;
  onClose: () => void;
  revokeRole: ReturnType<typeof useRevokeRole>;
  segmentNameById: Map<string, string>;
}) {
  const { t } = useTranslation();
  const roleLabels = useRoleLabels();
  const [error, setError] = useState<string | null>(null);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleConfirm() {
    if (!target) return;
    try {
      await revokeRole.mutateAsync({
        employeeId: target.user.id,
        roleId: target.role.id,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.settings.users.revokeError"));
    }
  }

  return (
    <Modal
      opened={Boolean(target)}
      onClose={handleClose}
      title={t("pages.settings.users.revokeTitle")}
      size="sm"
      centered
    >
      <Stack gap="md">
        {target && (
          <Text size="sm">
            {target.role.segmentId
              ? t("pages.settings.users.revokeQuestionWithSegment", {
                  name: target.user.fullName,
                  role: roleLabels[target.role.role],
                  segment: segmentNameById.get(target.role.segmentId) ?? "?",
                })
              : t("pages.settings.users.revokeQuestion", {
                  name: target.user.fullName,
                  role: roleLabels[target.role.role],
                })}
          </Text>
        )}
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={revokeRole.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            color="red"
            onClick={() => void handleConfirm()}
            loading={revokeRole.isPending}
          >
            {t("pages.settings.users.revokeSubmit")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Mitarbeiter anlegen (Admin) ──────────────────────────────────────────────

function CreateEmployeeModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const createEmployee = useCreateEmployee();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");

  const valid = email.includes("@") && fullName.trim().length > 0;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t("pages.settings.users.createEmployeeTitle")}
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t("pages.settings.users.createEmployeeHint")}
        </Text>
        <TextInput
          label={t("pages.settings.users.createEmployeeName")}
          value={fullName}
          onChange={(e) => setFullName(e.currentTarget.value)}
          required
        />
        <TextInput
          label={t("pages.settings.users.createEmployeeEmail")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!valid}
            loading={createEmployee.isPending}
            onClick={() =>
              createEmployee.mutate(
                { email: email.trim(), fullName: fullName.trim() },
                {
                  onSuccess: () => {
                    setEmail("");
                    setFullName("");
                    onClose();
                  },
                },
              )
            }
          >
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Mitarbeiter-Profil (Admin) ───────────────────────────────────────────────

/** Karrierestufen (aus der Excel-Steuerung) — Auswahlliste, kein Zwang. */
const CAREER_LEVELS = [
  "Professional Consultant",
  "Expert Consultant",
  "Senior Consultant",
  "Managing Consultant",
  "Manager",
  "Senior Manager",
  "Bereichsleiter",
];

function EmployeeProfileModal({
  target,
  onClose,
}: {
  target: UserListItem | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: profile } = useEmployeeProfile(target?.id ?? null);
  const { data: segments } = useSegments();
  const { data: allEmployees } = useEmployees();
  const saveProfile = useSaveEmployeeProfile();

  const [careerLevel, setCareerLevel] = useState<string | null>(null);
  const [federalState, setFederalState] = useState<string>("BE");
  const [hoursPerDay, setHoursPerDay] = useState<number | string>(8);
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [mentorId, setMentorId] = useState<string | null>(null);
  const [yppDate, setYppDate] = useState<string>("");

  // Beim Öffnen/Neuladen die gespeicherten Werte übernehmen.
  useEffect(() => {
    if (profile) {
      setCareerLevel(profile.careerLevel);
      setFederalState(profile.federalState);
      setHoursPerDay(Number(profile.hoursPerDay));
      setSegmentId(profile.segmentId);
      setMentorId(profile.mentorEmployeeId);
      setYppDate(profile.yppDate ?? "");
    }
  }, [profile, target?.id]);

  return (
    <Modal
      opened={target !== null}
      onClose={onClose}
      title={
        target
          ? t("pages.settings.users.profileTitle", { name: target.fullName })
          : ""
      }
    >
      <Stack gap="sm">
        <Select
          label={t("pages.settings.users.profileCareerLevel")}
          value={careerLevel}
          data={CAREER_LEVELS}
          searchable
          clearable
          onChange={setCareerLevel}
        />
        <Group grow>
          <Select
            label={t("pages.settings.users.profileFederalState")}
            value={federalState}
            data={[...FEDERAL_STATES]}
            searchable
            onChange={(v) => v && setFederalState(v)}
          />
          <NumberInput
            label={t("pages.settings.users.profileHoursPerDay")}
            value={hoursPerDay}
            onChange={setHoursPerDay}
            min={0}
            max={12}
            step={0.5}
          />
        </Group>
        <Select
          label={t("pages.settings.users.profileSegment")}
          value={segmentId}
          data={(segments ?? []).map((s) => ({ value: s.id, label: s.name }))}
          clearable
          onChange={setSegmentId}
        />
        <Group grow>
          <Select
            label={t("pages.settings.users.profileMentor")}
            value={mentorId}
            data={(allEmployees ?? [])
              .filter((e) => e.id !== target?.id)
              .map((e) => ({ value: e.id, label: e.fullName }))}
            searchable
            clearable
            onChange={setMentorId}
          />
          <TextInput
            label={t("pages.settings.users.profileYpp")}
            type="date"
            value={yppDate}
            onChange={(e) => setYppDate(e.currentTarget.value)}
          />
        </Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            loading={saveProfile.isPending}
            onClick={() =>
              target &&
              saveProfile.mutate(
                {
                  employeeId: target.id,
                  careerLevel,
                  federalState,
                  hoursPerDay: Number(hoursPerDay || 8),
                  segmentId,
                  mentorEmployeeId: mentorId,
                  yppDate: yppDate || null,
                },
                { onSuccess: onClose },
              )
            }
          >
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
